import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NavigationService } from '@/services/navigationService';
import { NavigationSettings, DEFAULT_NAVIGATION_SETTINGS } from '@/types/navigationTypes';
import { toast } from 'sonner';

export const useNavigationSettings = () => {
  const queryClient = useQueryClient();

  // Fetch navigation settings
  const {
    data: navigationSettings = DEFAULT_NAVIGATION_SETTINGS,
    isLoading,
    error
  } = useQuery({
    queryKey: ['navigation-settings'],
    queryFn: NavigationService.getNavigationSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Check permissions - but only after auth is ready
  const {
    data: canManage = false,
    isLoading: isCheckingPermissions
  } = useQuery({
    queryKey: ['navigation-permissions'],
    queryFn: NavigationService.canManageNavigation,
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: true, // Always enabled, let the service handle auth checks
  });

  // Update navigation settings mutation
  const updateMutation = useMutation({
    mutationFn: NavigationService.updateNavigationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['navigation-settings'] });
      toast.success('Navigation settings updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update navigation settings: ${error.message}`);
    },
  });

  const updateNavigationSettings = (settings: NavigationSettings) => {
    updateMutation.mutate(settings);
  };

  return {
    navigationSettings,
    canManage,
    isLoading,
    isCheckingPermissions,
    isUpdating: updateMutation.isPending,
    updateNavigationSettings,
    error,
  };
};

// Simplified hook for just checking if a feature is enabled
export const useFeatureEnabled = (feature: keyof NavigationSettings): boolean => {
  const { navigationSettings } = useNavigationSettings();
  return navigationSettings[feature] ?? true; // Default to enabled if not set
};